import { defineStore } from "pinia";
import { GDT } from "@/types";
import { makeGeometryFilter, titleCase } from "@/util/helpers";
import { useGammaData, useMap, useOrgSettings } from "@/services";
import { get, isEqual, zipObject } from "lodash-es";
import { watch } from "vue";
import { MultiPolygon, Polygon } from "geojson";

interface Paging {
  page: number;
  size: number;
}

type SpatialFilterType = "screen" | "polygon";

const reset = {
  dataRequest: {
    filter: [],
    orderBys: [],
    displayColumns: [],
    groupBys: [],
    paging: { page: 1, size: 25 },
    catalogUri: "",
  },
  data: {
    rows: [] as unknown[],
    totalAvailableRows: 0,
    totalAvailablePages: 0,
  },
};

interface DataRecords {
  rows: any[];
  totalAvailableRows: number;
  totalAvailablePages: number;
}

interface DataSourceState {
  name: string;
  layers: string[];
  isLoading: boolean;
  isActive: boolean;
  tableDefinition: null | GDT.TableDefinition;
  paging: Paging;
  usePolygonFilter: boolean;
  useScreenFilter: boolean;
  groupBys: GDT.GroupBy[];
  orderBys: GDT.OrderBy[];
  source: GDT.GammaDataSourceRaw;
  userFilters: GDT.DataFilter[];
  latestGeoJsonRequest: null | GDT.DataRequest;
  data: DataRecords;
}

export function useDataSource(name: string) {
  return defineStore(`${name}-source`, {
    state: (): DataSourceState => ({
      name: "",
      layers: [],
      isLoading: true,
      isActive: false,
      tableDefinition: null,
      paging: { page: 1, size: 25 },
      usePolygonFilter: true,
      useScreenFilter: true,
      groupBys: [],
      orderBys: [],
      source: {} as GDT.GammaDataSourceRaw,
      userFilters: [],
      latestGeoJsonRequest: null,
      data: reset.data,
    }),
    getters: {
      geometryKey(): string {
        return (
          this.columnDefinitions.find((c) => c.type === "Geometry")
            ?.columnName || "geom"
        );
      },
      screenFilter(): GDT.DataFilter | null {
        return useMap().screenBoundary
          ? makeGeometryFilter(
              this.geometryKey,
              useMap().screenBoundary as Polygon | MultiPolygon
            )
          : null;
      },
      polygonFilter(): GDT.DataFilter | null {
        return useMap().activeDatasetBoundary
          ? makeGeometryFilter(
              this.geometryKey,
              useMap().activeDatasetBoundary as Polygon | MultiPolygon
            )
          : null;
      },
      isVisible(): boolean {
        /** Todo: !!! */
        return (
          useMap().layerControls.find((c) => c.id === this.name)?.isVisible ||
          false
        );
      },
      type(): "Policy" | "Claim" | undefined {
        return this.source.dataSetType === "Policies"
          ? "Policy"
          : this.source.dataSetType === "Claims"
          ? "Claim"
          : undefined;
      },
      rows(): Record<string, unknown>[] {
        const displayCols = this.tableDefinition?.request.displayColumns
          ? this.tableDefinition.request.displayColumns
          : this.dataRequest().displayColumns;

        const colNames = displayCols.map((c: any) => c.name);
        return this.data.rows.map((row) => zipObject(colNames, row));
      },
      totalAvailableRows(): number {
        return this.data.totalAvailableRows || 0;
      },
      totalAvailablePages(): number {
        return this.data.totalAvailablePages || 0;
      },
      dataRequest(): (
        type?: "paged" | "spatial" | "accumulation"
      ) => GDT.GetDataRequest {
        const base = {
          ...(this.source.dataRequest || {
            ...reset.dataRequest,
          }),
        };
        return (queryType = "paged") => ({
          ...base,
          catalogUri: this.catalogUri,
          filter: this.allFilters(queryType),
          orderBys: this.orderBys.length ? this.orderBys : this.defaultOrderBys,
          groupBys: this.groupBys,
          paging:
            queryType === "spatial" || queryType === "accumulation"
              ? undefined
              : this.paging,
          displayColumns: base.displayColumns.map((col, k) => ({
            ...col,
            orderInSelectList: k + 1,
          })),
        });
      },
      defaultOrderBys(): GDT.OrderBy[] {
        return [
          {
            columnName:
              get(this, "dataSetDefinition.columns[0].columnName") ||
              get(this, "source.dataRequest.displayColumns.0.name") ||
              "",
            keyword: GDT.OrderKeyword.Asc,
          },
        ];
      },
      allFilters(): (queryType: string) => GDT.DataFilter[] {
        return (t) => {
          return (this.source.dataRequest?.filter || [])
            .concat(
              t === "spatial" ? this.spatialFiltersQuery : this.spatialFilters
            )
            .concat(this.userFilters);
        };
      },
      spatialFilters(): GDT.DataFilter[] {
        return ([] as GDT.DataFilter[])
          .concat(
            this.usePolygonFilter && this.polygonFilter
              ? [this.polygonFilter]
              : []
          )
          .concat(
            this.useScreenFilter && this.screenFilter ? [this.screenFilter] : []
          );
      },
      spatialFiltersQuery(): GDT.DataFilter[] {
        return ([] as GDT.DataFilter[])
          .concat(this.polygonFilter ? [this.polygonFilter] : [])
          .concat(this.screenFilter ? [this.screenFilter] : []);
      },
      catalogUri(): string {
        return this.source.dataRequest?.catalogUri.trim() || "";
      },
      dataSetDefinition(): GDT.DatasetDefinition | undefined {
        return this.tableDefinition?.dataSetDefinition;
      },
      columnDefinitions(): GDT.ColumnDefinition[] {
        return this.dataSetDefinition?.columns || [];
      },
      bottomColumnDefinitions(): GDT.ColumnDefinition[] {
        const dataDisplay = useOrgSettings().dataDisplayDict.get(this.name);

        if (this.dataSetDefinition && dataDisplay) {
          const columnsMap = this.dataSetDefinition.columns.reduce(
            (x, y) => x.set(y.columnName, y),
            new Map<string, GDT.ColumnDefinition>()
          );

          return dataDisplay.columns
            .filter((x) => x.showInBottomPanel && columnsMap.has(x.columnName))
            .map(
              (x) =>
                ({
                  ...columnsMap.get(x.columnName),
                  displayName: x.displayName,
                  visible: x.selectedBottomPanel,
                } as GDT.ColumnDefinition)
            );
        }
        return this.dataSetDefinition?.columns || [];
      },
      columnDefinitionsMap(): Map<string, GDT.ColumnDefinition> {
        return this.columnDefinitions.reduce(
          (result, item) => result.set(item.columnName, item),
          new Map<string, GDT.ColumnDefinition>()
        );
      },
      uniqueIdColumns(): GDT.ColumnDefinition[] {
        return this.columnDefinitions.filter((c) => c.context === "UniqueId");
      },
      visibleColumns(): { name: string; visible: boolean }[] {
        return this.columnDefinitions
          .filter((c) => c.visible)
          .map((c) => ({
            name: c.columnName,
            visible:
              this.tableDefinition?.request.displayColumns
                .map((dc) => dc.name)
                .includes(c.columnName) || false,
          }));
      },
    },
    actions: {
      async init(data: GDT.IGammaDataSource) {
        Object.assign(this, data);
        await this.td();

        this.isLoading = false;
        watch(
          () => this.isActive,
          (val) => val && this.fetchData(),
          { immediate: true }
        );
      },
      async td() {
        return this.tableDefinition
          ? Promise.resolve(this.tableDefinition)
          : this.fetchMeta().then(() =>
              Promise.resolve(Promise.resolve(this.tableDefinition))
            );
      },
      async getAllRowsForExport() {
        const rawData = await useGammaData().fetchData({
          ...this.dataRequest("accumulation"),
        });
        const displayCols = this.tableDefinition?.request.displayColumns
          ? this.tableDefinition.request.displayColumns
          : this.dataRequest().displayColumns;
        const labelDictionary =
          this.tableDefinition?.dataSetDefinition.columns.reduce(
            (acc, col) => acc.set(col.columnName, col.displayName),
            new Map<string, string>()
          );
        const colNames = displayCols.map(
          (c: any) => titleCase(labelDictionary?.get(c.name) || c.name) || ""
        );
        return rawData.data.data.rows.map((row) => {
          const obj = zipObject(colNames, row);
          delete obj.Geom;
          return obj;
        });
      },
      async fetchMeta() {
        let { data } = await useGammaData().fetchMeta({
          ...this.source.dataRequest,
          catalogUri: this.catalogUri,
        });

        if (data && !data.request?.displayColumns.length) {
          data = { ...data, request: { ...(data.request || {}) } };
          data.request.displayColumns =
            data.dataSetDefinition.columns.map((x, i) => ({
              name: x.columnName,
              label: x.displayName,
              orderInSelectList: i,
              visible: true,
            })) || [];
        }

        this.tableDefinition = data;
      },
      async fetchData(
        dr: Partial<GDT.GetDataRequest> = {},
        {
          shouldUpdateData,
          requestType,
        }: {
          shouldUpdateData: boolean;
          requestType: "paged" | "spatial" | "accumulation";
        } = {
          shouldUpdateData: true,
          requestType: "paged",
        },
        queue = "table"
      ): Promise<Partial<GDT.Data>> {
        this.isLoading = true;
        return this.td()
          .then(() =>
            useGammaData().fetchData(
              { ...this.dataRequest(requestType), ...dr },
              queue
            )
          )
          .then(({ data }) => {
            if (data) {
              const { rows, totalAvailableRows, totalAvailablePages } =
                data.data || {};
              if (shouldUpdateData) {
                this.update("data", {
                  rows,
                  totalAvailableRows,
                  totalAvailablePages,
                });
              }
              if (requestType === "paged") {
                this.update("paging", {
                  ...this.paging,
                  page: data.data.currentPage,
                });
              }
              this.isLoading = false;
              return { rows, totalAvailableRows, totalAvailablePages };
            }
            return { rows: [], totalAvailableRows: 0, totalAvailablePages: 0 };
          });
      },
      update(prop: keyof DataSourceState, val: unknown, forceUpdate = false) {
        if (!isEqual(this[prop], val) || forceUpdate) {
          Object.assign(this, { [prop]: val });
          if (["paging", "filter", "orderBys"].includes(prop) || forceUpdate) {
            this.fetchData().then();
          }
        }
      },
      addFilter(filter: GDT.DataFilter) {
        const filters = this.userFilters;
        const index = filters.findIndex((f) => isEqual(f, filter));
        if (index === -1) {
          filters.push(filter);
          this.update("userFilters", filters, true);
        }
      },
      removeFilter(filter: GDT.DataFilter | undefined, i?: number) {
        const filters = this.userFilters;
        const index = filter ? filters.findIndex((f) => isEqual(f, filter)) : i;
        if (
          typeof index === "number" &&
          index > -1 &&
          filters.length >= index + 1
        ) {
          filters.splice(index, 1);
          this.update("userFilters", filters, true);
        }
      },
      updateFilter(filter: GDT.DataFilter, index: number) {
        const filters = this.userFilters;
        if (filters.length >= index + 1) {
          filters.splice(index, 1, filter);
          this.update("userFilters", filters, true);
        }
      },
      setSpatialFilter(value = false, type: SpatialFilterType = "screen") {
        if (type === "screen") {
          this.useScreenFilter = value;
        } else {
          this.usePolygonFilter = value;
        }
      },
    },
  });
}
